import React, { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

const cn = (...inputs) => twMerge(clsx(inputs));
const SearchBar = ({
  value,
  onChange,
  setFilterValue,
  placeholder = "Search...",
  icon: Icon = Search,
  className = "",
  disabled = false,
  debounceDelay = 500,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const debounceTimerRef = useRef(null);
  const inputRef = useRef(null);

  // Prevents parent re-renders
  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setLocalValue(value);
    }
  }, [value]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setFilterValue(newValue);
      if (onChange) {
        onChange(newValue);
      }
    }, debounceDelay);
  };

  const handleClearClick = () => {
    setLocalValue("");

    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    setFilterValue("");
    if (onChange) {
      onChange("");
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div className={cn("relative w-full min-w-0", className)}>
      <div
        className={cn(
          "group relative box-border flex w-full min-w-0 items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/90 px-3.5 py-2.5  shadow-xs shadow-gray-200 dark:shadow-none",
          "focus-within:border-[#207C97] focus-within:ring-1 focus-within:ring-[#207C97]",
          disabled ? "cursor-not-allowed opacity-50" : "cursor-text",
        )}
      >
        <Icon
          size={18}
          className="shrink-0 text-gray-300 dark:text-gray-400 transition-colors group-focus-within:text-[#207C97]"
        />
        <input
          ref={inputRef}
          type="text"
          value={localValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          className="min-w-0 flex-1 w-full bg-transparent text-sm text-gray-800 dark:text-gray-100 focus:outline-none placeholder:overflow-hidden placeholder:text-ellipsis placeholder:text-sm placeholder:font-normal placeholder:text-gray-400 dark:placeholder:text-gray-400"
        />
        {localValue ? (
          <button
            type="button"
            onClick={handleClearClick}
            className="shrink-0 text-gray-500 dark:text-gray-400 transition hover:text-gray-900 dark:hover:text-gray-100"
            aria-label="Clear input"
          >
            <X size={17} />
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default SearchBar;
